import type React from 'react';
import { useState } from 'react';
import { Card, CardContent, CardActions, Typography, IconButton, Collapse, styled, Box } from '@mui/material';
import { ReactComponent as CheckBlueLargeIcon } from '../../../assets/icons/check-blue-large.svg';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface CollapsibleCardProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  className?: string;
  icon?: React.ReactNode | null;
}

const ExpandMore = styled(({ expand, ...other }: { expand: boolean } & React.ComponentProps<typeof IconButton>) => (
  <IconButton {...other} />
))(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

export default function CollapsibleCard({
  title,
  children,
  defaultExpanded = false,
  className,
  icon,
}: CollapsibleCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Card className={className} sx={{ width: '100%', overflow: 'visible' }}>
      <CardActions disableSpacing sx={{ padding: 2, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ marginRight: '16px' }}>{icon ? icon : <CheckBlueLargeIcon />}</Box>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontSize: '16px', fontWeight: 600 }}>
          {title}
        </Typography>
        <ExpandMore expand={expanded} onClick={handleExpandClick} aria-expanded={expanded} aria-label="show more">
          <ExpandMoreIcon />
        </ExpandMore>
      </CardActions>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ paddingTop: 0 }}>{children}</CardContent>
      </Collapse>
    </Card>
  );
}
